import React, { useCallback, useEffect, useState } from 'react';

interface LineHeightInputProps {
  /** Unitless line-height multiplier; null = Auto (font default). */
  value: number | null;
  onChange: (value: number | null) => void;
  /**
   * Value seeded when stepping up/down from Auto — the approximate
   * effective line-height of the element's current default rendering.
   */
  autoSeed?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Optional title for accessibility */
  title?: string;
}

const STEP = 0.05;

/** Format without trailing zeroes: 1.2 -> "1.2", 1.25 -> "1.25", 1.5 -> "1.5". */
const formatLineHeight = (n: number): string => String(parseFloat(n.toFixed(2)));

/**
 * Line height input with editable field plus stacked +/- buttons.
 * Mirrors FontSizeInput but supports the "Auto" sentinel (null) and
 * decimal multipliers.
 * - Displays "Auto" when unset; stepping from Auto seeds `autoSeed`
 * - Up/Down arrow keys and +/- buttons step by 0.05
 * - Typing a number sets it; typing "auto" (or clearing) returns to Auto
 * - Value commits on blur or Enter
 */
const LineHeightInput: React.FC<LineHeightInputProps> = ({
  value,
  onChange,
  autoSeed = 1.25,
  min = 0.5,
  max = 3,
  disabled = false,
  title = 'Line height',
}) => {
  const toDisplay = useCallback(
    (v: number | null) => (v == null ? 'Auto' : formatLineHeight(v)),
    [],
  );

  const [draft, setDraft] = useState<string>(toDisplay(value));

  useEffect(() => {
    setDraft(toDisplay(value));
  }, [value, toDisplay]);

  const clamp = useCallback(
    (n: number) => parseFloat(Math.min(max, Math.max(min, n)).toFixed(2)),
    [min, max],
  );

  const step = useCallback(
    (dir: 1 | -1) => {
      const base = value ?? autoSeed;
      // From Auto, the first activation lands on the seed itself rather
      // than seed±step, so the user sees the current effective value.
      const next = value == null ? clamp(base) : clamp(base + dir * STEP);
      if (next !== value) onChange(next);
    },
    [value, autoSeed, clamp, onChange],
  );

  const commit = useCallback(() => {
    const trimmed = draft.trim().toLowerCase();
    if (trimmed === '' || trimmed === 'auto') {
      if (value !== null) onChange(null);
      setDraft(toDisplay(null));
      return;
    }
    const parsed = parseFloat(trimmed);
    if (Number.isFinite(parsed)) {
      const next = clamp(parsed);
      if (next !== value) onChange(next);
      setDraft(formatLineHeight(next));
    } else {
      setDraft(toDisplay(value));
    }
  }, [draft, value, onChange, clamp, toDisplay]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      step(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      step(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div
      className="flex items-stretch"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        inputMode="decimal"
        value={draft}
        // Allow digits/decimal point plus any prefix of "auto" so the
        // sentinel can be typed; commit() validates the final value.
        onChange={(e) => {
          const raw = e.target.value;
          if (/^\d*\.?\d*$/.test(raw) || /^(a|au|aut|auto)$/i.test(raw)) {
            setDraft(raw);
          }
        }}
        onFocus={(e) => e.target.select()}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        title={title}
        aria-label={title}
        className="w-10 h-6 text-[11px] leading-none text-center bg-black/40 text-white rounded-l border border-r-0 border-white/20 focus:outline-none focus:border-white/60"
        style={{ paddingInline: 0 }}
      />
      <div className="flex flex-col">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); step(1); }}
          disabled={disabled}
          aria-label="Increase line height"
          className="w-3 h-3 leading-none text-[10px] text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-tr border border-white/20 border-b-0 flex items-center justify-center"
        >
          +
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); step(-1); }}
          disabled={disabled}
          aria-label="Decrease line height"
          className="w-3 h-3 leading-none text-[10px] text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-br border border-white/20 flex items-center justify-center"
        >
          −
        </button>
      </div>
    </div>
  );
};

export default LineHeightInput;
